<template>
  <v-card>
    <div class="pa-6">
      <div class="d-flex justify-end align-center pb-6">
        <v-btn class="mr-6" color="green" dark outlined @click="exportDataToExcel">
          <v-icon left>mdi-file-excel-outline</v-icon>Xuất Excel</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn color="primary" class="elevation-0 mr-6" dark @click="gradeAnnounce">
          Công bố điểm
        </v-btn>
        <v-btn color="primary" class="elevation-0" dark @click="teacherAnnounce">
          Công bố cho G.V
        </v-btn>
      </div>
      <v-card>
        <v-data-table
          item-key="id"
          :headers="computedHeaders"
          :loading="loading"
          :items="marks"
          :footer-props="{ 'items-per-page-text': 'Số học sinh mỗi trang' }"
          v-bind="this.$attrs"
          fixed-header
        >
          <template v-for="subject in this.subjectsToUse" v-slot:[`item.subject_${subject.id}`]="{ item }">
            <v-tooltip bottom :key="subject.id">
              <template v-slot:activator="{ on, attrs }">
                <span :style="item[`markChanged_${subject.id}`] ? 'color: red;' : ''" v-bind="attrs" v-on="on">
                  {{ item[`subject_${subject.id}`] }}
                </span>
              </template>
              <div v-for="change in formatChangeLog(item[`changeLog_${subject.id}`])" :key="change">
                {{ change }}
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import * as XLSX from 'xlsx'
import { ExamMark } from '@/plugins/api'

const originHeaders = [
  {
    text: 'Học sinh',
    value: 'studentName',
    align: 'left',
    sortable: false,
    width: '15%'
  },
  {
    text: 'Số báo danh',
    value: 'examCode',
    align: 'left',
    sortable: false,
    width: '10%'
  },
  {
    text: 'Phòng',
    value: 'rooms',
    align: 'left',
    sortable: false,
    width: '10%'
  }
]

export default {
  props: {
    room: Object,
    query: Object,
    teacher: Object,
    subjects: Array
  },
  data() {
    return {
      loading: false,
      headers: originHeaders,
      subjectHeaders: [],
      marks: [],
      json_fields: {
        'Số báo danh': 'examCode',
        'Họ tên': 'name',
        'Mã HS': 'studentCode'
      },
      markMapping: {
        absent: 'Vắng thi',
        present: 'Có thi',
        emtpy: 'Không biên bản',
        suspended: 'VPQC'
      }
    }
  },
  computed: {
    computedHeaders() {
      if (this.$auth.role.type === 'teacher') {
        const teacherSubjects = this.teacher.subjects.map(subject => subject.id)
        const filteredSubjectHeaders = this.subjectHeaders.filter(header =>
          teacherSubjects.includes(header.value.split('_')[1])
        )
        return [...this.headers, ...filteredSubjectHeaders]
      }
      return [...this.headers, ...this.subjectHeaders]
    },
    subjectsToUse() {
      return this.room ? this.room.subjects : this.subjects
    }
  },
  mounted() {
    this.updateSubjectHeaders(this.room)
  },
  methods: {
    ...mapActions('examMark', ['fetchMarks', 'countMarks']),
    ...mapActions('exam', ['updateExam']),
    async refresh() {
      this.$loading.active = true
      this.updateSubjectHeaders(this.room)
      this.loading = true
      try {
        const rawMarks = await this.fetchMarks({
          ...this.query,
          _limit: -1,
          _sort: 'examCode:ASC'
        })
        this.marks = this.transformMarksData(rawMarks, this.subjectsToUse)
      } catch (error) {
        console.log(error)
        this.$alert.error('Lỗi load thông tin học sinh')
      } finally {
        this.loading = false
        this.$loading.active = false
      }
    },
    async gradeAnnounce() {
      this.$loading.active = true
      const update = await this.updateExam({ id: this.query.exam, hasGrade: 'true' })
      if (update) {
        this.$alert.success('Công bố điểm thành công')
      } else {
        this.$alert.error('Lỗi công bố điểm')
      }
      this.$loading.active = false
    },
    async teacherAnnounce() {
      this.$loading.active = true
      const update = await this.updateExam({ id: this.query.exam, hasTeacherGrade: 'true' })
      if (update) {
        this.$alert.success('Công bố điểm thành công')
      } else {
        this.$alert.error('Lỗi công bố điểm')
      }
      this.$loading.active = false
    },
    updateSubjectHeaders(room) {
      const subjects = room ? room.subjects : this.subjects
      this.subjectHeaders = subjects.map(subject => ({
        text: subject.name,
        value: `subject_${subject.id}`,
        align: 'left',
        sortable: false,
        width: '10%'
      }))
    },
    formatChangeLog(changeLog) {
      if (!changeLog) return []

      return Object.entries(changeLog).map(([key, value]) => {
        const [time, status] = key.split('_')
        const [name, mark] = value.split('_')
        let action

        if (status === 'first') {
          action = 'đã nhập điểm'
        } else if (status === 'review') {
          action = 'đã phúc khảo điểm sang'
        } else {
          action = 'đã đổi điểm sang'
        }
        return `${time}: ${name} ${action} ${mark}`
      })
    },

    transformMarksData(marks, subjects) {
      const studentData = {}

      marks.forEach(mark => {
        if (!studentData[mark.student.id]) {
          studentData[mark.student.id] = {
            studentName: mark.student.name,
            dob: mark.student.dob,
            examCode: mark.examCode,
            rooms: new Set(),
            ...subjects.reduce(
              (acc, subject) => ({
                ...acc,
                [`subject_${subject.id}`]: '-',
                [`markChanged_${subject.id}`]: '',
                [`changeLog_${subject.id}`]: ''
              }),
              {}
            )
          }
        }

        studentData[mark.student.id][`subject_${mark.subject.id}`] =
          mark.status === 'present' ? mark.value : this.markMapping[mark.status]
        studentData[mark.student.id][`markChanged_${mark.subject.id}`] = mark.markChanged
        studentData[mark.student.id][`changeLog_${mark.subject.id}`] = mark.changeLog
        studentData[mark.student.id].rooms.add(mark.room.name)
      })

      Object.keys(studentData).forEach(studentId => {
        studentData[studentId].rooms = Array.from(studentData[studentId].rooms).join(', ')
      })

      return Object.values(studentData)
    },
    async exportDataToExcel() {
      this.loading = true
      try {
        const data = await ExamMark.getMarksExcel({ exam: this.query.exam })

        const formattedData = this.mapFields(data, this.json_fields)
        this.addDynamicFields(formattedData, data)

        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        this.setColumnWidths(worksheet, formattedData)

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Marks')

        XLSX.writeFile(workbook, `diem_thi.xlsx`)
      } catch (error) {
        console.error('Error exporting data to Excel:', error)
      } finally {
        this.loading = false
      }
    },
    mapFields(data, fieldMap) {
      return data.map(item => {
        const newItem = {}
        for (const [key, value] of Object.entries(fieldMap)) {
          newItem[key] = item[value]
        }
        return newItem
      })
    },
    addDynamicFields(formattedData, data) {
      if (data.length > 0) {
        const additionalFields = Object.keys(data[0]).filter(key => !Object.values(this.json_fields).includes(key))
        formattedData.forEach((item, index) => {
          additionalFields.forEach(field => {
            item[field] = data[index][field]
          })
        })
      }
    },
    setColumnWidths(worksheet, data) {
      let maxWidths = []
      const columnNames = data.length > 0 ? Object.keys(data[0]) : []

      columnNames.forEach((name, index) => {
        maxWidths[index] = name.length
      })

      data.forEach(row => {
        columnNames.forEach((name, index) => {
          const value = (row[name] || '').toString().trim()
          maxWidths[index] = Math.max(maxWidths[index], value.length)
        })
      })

      worksheet['!cols'] = maxWidths.map(width => {
        return { width }
      })
    }
  },
  watch: {
    query: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.refresh()
        }
      }
    }
  }
}
</script>
